import {
  addMonths,
  addYears,
  format,
  getYear,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  isWithinInterval,
  setYear,
  startOfMonth,
  startOfWeek,
  subMonths,
  subYears,
} from 'date-fns'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components'
import { cn } from '@/utils'

type DateRange = {
  from?: Date
  to?: Date
}

type CalendarMode = 'single' | 'range'

interface DayCellProps {
  date: Date
  monthDate: Date
  mode: CalendarMode
  selected?: Date | DateRange
  isDisabled: boolean
  onSelect?: (value: Date | DateRange) => void
}

const DayCell = React.memo(function DayCell({
  date,
  monthDate,
  mode,
  selected,
  isDisabled,
  onSelect,
}: DayCellProps) {
  const rangeSelection = mode === 'range' ? (selected as DateRange) : null
  const isSelectedDay =
    mode === 'range'
      ? (rangeSelection?.from && isSameDay(date, rangeSelection.from)) ||
        (rangeSelection?.to && isSameDay(date, rangeSelection.to))
      : selected && isSameDay(date, selected as Date)

  const isRangeMiddle =
    mode === 'range' &&
    rangeSelection?.from &&
    rangeSelection?.to &&
    isWithinInterval(date, {
      start: rangeSelection.from,
      end: rangeSelection.to,
    }) &&
    !isSelectedDay

  const isOutside = !isSameMonth(date, monthDate)
  const isRangeStart =
    mode === 'range' &&
    rangeSelection?.from &&
    isSameDay(date, rangeSelection.from)
  const isRangeEnd =
    mode === 'range' && rangeSelection?.to && isSameDay(date, rangeSelection.to)

  const handleClick = () => {
    if (isDisabled) return

    if (mode === 'single') {
      onSelect?.(date)
    } else if (mode === 'range') {
      const rangeSelected = selected as DateRange
      if (
        rangeSelected?.from &&
        !rangeSelected?.to &&
        date >= rangeSelected.from
      ) {
        onSelect?.({ from: rangeSelected.from, to: date })
      } else {
        onSelect?.({ from: date, to: undefined })
      }
    }
  }

  return (
    <div
      className={cn(
        'h-9 w-9 text-center text-sm p-0 relative',
        isRangeMiddle && 'bg-accent/50 first:rounded-l-md last:rounded-r-md',
        isRangeStart && 'rounded-l-md',
        isRangeEnd && 'rounded-r-md',
      )}
    >
      <Button
        theme="ghost"
        disabled={isDisabled}
        className={cn(
          'h-9 w-9 p-0 font-normal aria-selected:opacity-100',
          isSelectedDay &&
            'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
          isToday(date) && !isSelectedDay && 'bg-accent text-accent-foreground',
          (isOutside || isDisabled) && 'text-muted-foreground opacity-50',
          isRangeMiddle &&
            'bg-transparent text-accent-foreground hover:bg-transparent',
        )}
        onClick={handleClick}
      >
        {format(date, 'd')}
      </Button>
    </div>
  )
})

type DisabledDate = Date | Date[] | { before?: Date; after?: Date }

interface CalendarProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onSelect'> {
  mode?: CalendarMode
  selected?: Date | DateRange
  onSelect?: (value: Date | DateRange | undefined) => void
  initialFocus?: boolean
  numberOfMonths?: number
  defaultMonth?: Date
  month?: Date
  disabled?: DisabledDate
}

function Calendar({
  className,
  mode = 'single',
  selected,
  onSelect,
  initialFocus,
  numberOfMonths = 1,
  defaultMonth,
  month: controlledMonth,
  disabled,
  ...props
}: CalendarProps) {
  const [currentMonth, setCurrentMonth] = React.useState<Date>(
    defaultMonth || new Date(),
  )
  const [view, setView] = React.useState<'days' | 'years'>('days')

  React.useEffect(() => {
    if (controlledMonth) setCurrentMonth(controlledMonth)
  }, [controlledMonth])

  const upperBound = React.useMemo(() => {
    if (
      disabled &&
      typeof disabled === 'object' &&
      !Array.isArray(disabled) &&
      !(disabled instanceof Date) &&
      disabled.after
    ) {
      return disabled.after
    }
    return new Date()
  }, [disabled])

  const lowerBound = React.useMemo(() => {
    if (
      disabled &&
      typeof disabled === 'object' &&
      !Array.isArray(disabled) &&
      !(disabled instanceof Date) &&
      disabled.before
    ) {
      return disabled.before
    }
    return undefined
  }, [disabled])

  const canGoForward =
    currentMonth.getFullYear() < upperBound.getFullYear() ||
    (currentMonth.getFullYear() === upperBound.getFullYear() &&
      currentMonth.getMonth() < upperBound.getMonth())

  const canGoBack = lowerBound
    ? currentMonth.getFullYear() > lowerBound.getFullYear() ||
      (currentMonth.getFullYear() === lowerBound.getFullYear() &&
        currentMonth.getMonth() > lowerBound.getMonth())
    : true

  const nextMonth = () => {
    if (canGoForward) setCurrentMonth(addMonths(currentMonth, 1))
  }
  const prevMonth = () => {
    if (canGoBack) setCurrentMonth(subMonths(currentMonth, 1))
  }

  const nextYears = () => {
    const next = addYears(currentMonth, 12)
    if (next.getFullYear() - 6 <= upperBound.getFullYear())
      setCurrentMonth(next)
  }
  const prevYears = () => {
    if (!lowerBound) {
      setCurrentMonth(subYears(currentMonth, 12))
      return
    }
    const prev = subYears(currentMonth, 12)
    if (prev.getFullYear() + 6 >= lowerBound.getFullYear())
      setCurrentMonth(prev)
    else setCurrentMonth(setYear(currentMonth, lowerBound.getFullYear()))
  }

  const isDateDisabled = (date: Date): boolean => {
    if (!disabled) return false

    if (disabled instanceof Date) {
      return isSameDay(date, disabled)
    }

    if (Array.isArray(disabled)) {
      return disabled.some((d) => isSameDay(date, d))
    }

    if (typeof disabled === 'object') {
      if (disabled.before && isBefore(date, disabled.before)) return true
      if (disabled.after && isAfter(date, disabled.after)) return true
    }

    return false
  }

  const renderMonth = (monthDate: Date) => {
    const monthStart = startOfMonth(monthDate)
    const startDate = startOfWeek(monthStart)

    const rows: React.ReactNode[] = []

    const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

    const header = (
      <div className="flex justify-center pt-1 relative items-center mb-4">
        <Button
          theme="ghost"
          size="sm"
          className="text-sm font-medium hover:bg-accent/50 group"
          onClick={() => setView('years')}
        >
          {format(monthDate, 'MMMM yyyy')}
          <ChevronDown className="ml-1 h-3 w-3 text-muted-foreground group-hover:text-foreground transition-colors" />
        </Button>
      </div>
    )

    const weekHeader = (
      <div className="flex mb-2">
        {weekDays.map((d, i) => (
          <div
            key={i}
            className="text-muted-foreground rounded-md w-9 font-normal text-[0.8rem] flex justify-center"
          >
            {d}
          </div>
        ))}
      </div>
    )

    const dayCells: Date[] = []
    let currentDate = startDate

    while (dayCells.length < 42) {
      dayCells.push(currentDate)
      currentDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate() + 1,
      )
    }

    for (let i = 0; i < 6; i++) {
      const rowDays = dayCells.slice(i * 7, (i + 1) * 7)
      rows.push(
        <div key={i} className="flex w-full mt-2">
          {rowDays.map((d, idx) => (
            <DayCell
              key={idx}
              date={d}
              monthDate={monthDate}
              mode={mode}
              selected={selected}
              isDisabled={isDateDisabled(d)}
              onSelect={onSelect}
            />
          ))}
        </div>,
      )
    }

    return (
      <div className="space-y-4 animate-in fade-in zoom-in-95 duration-200">
        {header}
        {weekHeader}
        <div className="w-full">{rows}</div>
      </div>
    )
  }

  const renderYears = () => {
    const currentYear = getYear(currentMonth)
    const lowerYear = lowerBound ? lowerBound.getFullYear() : -Infinity
    const upperYear = upperBound.getFullYear()
    const startYear = Math.max(currentYear - 6, lowerYear)
    const endYear = Math.min(currentYear + 5, upperYear)
    const years: number[] = []

    for (let i = startYear; i <= endYear; i++) {
      years.push(i)
    }

    return (
      <div className="space-y-4 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex justify-center pt-1 relative items-center mb-4">
          <Button
            theme="ghost"
            size="sm"
            className="text-sm font-medium hover:bg-accent/50"
            onClick={() => setView('days')}
          >
            {startYear} - {endYear}
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {years.map((year) => {
            const isCurrentYear = year === new Date().getFullYear()
            const isSelectedYear =
              (selected as DateRange)?.from &&
              getYear((selected as DateRange).from!) === year

            return (
              <Button
                key={year}
                theme={isSelectedYear ? 'default' : 'ghost'}
                className={cn(
                  'h-9 w-full text-sm font-normal',
                  isCurrentYear &&
                    !isSelectedYear &&
                    'bg-accent text-accent-foreground',
                )}
                onClick={() => {
                  setCurrentMonth(setYear(currentMonth, year))
                  setView('days')
                }}
              >
                {year}
              </Button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className={cn('p-3', className)} {...props}>
      <div className="flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0">
        {view === 'days' &&
          Array.from({ length: numberOfMonths }).map((_, i) => (
            <div key={i} className="space-y-4 relative">
              {i === 0 && (
                <Button
                  theme="outline"
                  className={cn(
                    'absolute left-1 top-0 h-7 w-7 bg-transparent p-0 z-10',
                    canGoBack
                      ? 'opacity-50 hover:opacity-100'
                      : 'opacity-20 cursor-not-allowed',
                  )}
                  disabled={!canGoBack}
                  onClick={prevMonth}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}

              {renderMonth(addMonths(currentMonth, i))}

              {i === numberOfMonths - 1 && (
                <Button
                  theme="outline"
                  className={cn(
                    'absolute right-1 top-0 h-7 w-7 bg-transparent p-0 z-10',
                    canGoForward
                      ? 'opacity-50 hover:opacity-100'
                      : 'opacity-20 cursor-not-allowed',
                  )}
                  disabled={!canGoForward}
                  onClick={nextMonth}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}

        {view === 'years' && (
          <div className="space-y-4 relative w-full min-w-[280px]">
            <Button
              theme="outline"
              className="absolute left-1 top-0 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 z-10"
              onClick={prevYears}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {renderYears()}

            <Button
              theme="outline"
              className="absolute right-1 top-0 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 z-10"
              onClick={nextYears}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
Calendar.displayName = 'Calendar'

export type { CalendarProps, DateRange }
export { Calendar }
